import { Link } from "react-router-dom";
import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "@/lib/store";
import { translations, BRAND } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom TikTok + Snapchat icons (lucide doesn't include them)
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.16a4.85 4.85 0 0 1-1.84-.47z" />
  </svg>
);
const SnapIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12.166.561c1.825 0 5.918.819 7.66 4.749.59 1.345.443 3.62.327 5.45l-.013.196c-.011.179-.022.355-.033.526a.65.65 0 0 0 .42.66c.31.124.66.054 1.06-.085.404-.141.797-.106 1.07.087.31.219.366.581.366.793 0 .668-.668 1.039-1.41 1.262-.075.023-.155.046-.236.07-.276.082-.65.193-.65.444 0 .055.022.117.054.181.151.305.95 1.808 3.198 2.36 1.182.288 1.106 1.105 1.106 1.182v.027c0 .386-.388.66-1.179.84-.66.155-1.45.236-2.243.305-.06.044-.11.181-.142.305-.022.107-.06.236-.116.388-.054.142-.18.236-.397.236h-.054c-.155 0-.366-.022-.622-.07-.4-.082-.835-.169-1.503-.169-.4 0-.811.034-1.214.107-.794.142-1.461.612-2.221 1.16-1.085.781-2.221 1.587-3.962 1.587-.075 0-.142 0-.211-.011-.092.011-.181.011-.27.011-1.741 0-2.877-.806-3.962-1.586-.76-.55-1.43-1.019-2.221-1.16-.4-.074-.814-.108-1.214-.108-.668 0-1.105.097-1.503.169-.25.046-.467.07-.622.07-.224 0-.366-.13-.42-.236-.054-.142-.092-.27-.116-.388-.034-.13-.082-.27-.142-.305-.794-.07-1.583-.15-2.243-.305-.79-.18-1.179-.456-1.179-.84v-.027c0-.077-.075-.894 1.106-1.182 2.247-.55 3.047-2.054 3.198-2.36.034-.064.054-.13.054-.181 0-.25-.366-.366-.65-.443-.082-.024-.16-.046-.236-.07-.84-.27-1.41-.595-1.41-1.262 0-.212.054-.574.366-.793.226-.15.51-.193.78-.13.108.027.205.06.292.094.404.135.74.207 1.044.094a.65.65 0 0 0 .42-.66c-.012-.171-.022-.347-.034-.526l-.012-.196c-.116-1.83-.27-4.105.327-5.45C6.236 1.387 10.33.561 12.166.561z" />
  </svg>
);

export default function Footer() {
  const { lang } = useStore();
  const t = translations[lang];
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, lang });
      toast.success(t.newsletter_thanks);
      setEmail("");
    } catch (err) {
      toast.error(t.contact_error);
    }
    setLoading(false);
  };

  const socials = [
    { Icon: Instagram, href: BRAND.instagram, label: "Instagram", id: "instagram" },
    { Icon: MessageCircle, href: `https://wa.me/${BRAND.whatsapp}`, label: "WhatsApp", id: "whatsapp" },
    { Icon: SnapIcon, href: BRAND.snapchat, label: "Snapchat", id: "snap" },
    { Icon: TikTokIcon, href: BRAND.tiktok, label: "TikTok", id: "tiktok" },
    { Icon: Facebook, href: BRAND.facebook, label: "Facebook", id: "facebook" },
  ];

  return (
    <footer
      className="border-t border-yellow-600/20 mt-24"
      style={{ background: "linear-gradient(180deg, #050505 0%, #0a0805 100%)" }}
      data-testid="site-footer"
    >
      {/* Newsletter band */}
      <div className="border-b border-yellow-600/15">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3
              className="text-3xl sm:text-4xl font-black"
              style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A", letterSpacing: "0.02em" }}
            >
              {t.newsletter_title}
            </h3>
            <p className="text-white/60 mt-2">{t.newsletter_subtitle}</p>
          </div>
          <form onSubmit={submitNewsletter} className="flex gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder={t.newsletter_placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
              className="flex-1 px-4 py-3 rounded-full bg-white/5 border border-yellow-500/30 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="px-6 py-3 rounded-full font-bold tracking-widest text-black disabled:opacity-50"
              style={{
                background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                boxShadow: "0 8px 20px -6px rgba(212,175,55,0.55)",
              }}
            >
              {t.newsletter_cta}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/logos/braf_zak_logo_main.jpeg"
              alt="Braf_Zak"
              className="w-12 h-12 rounded-full object-cover border border-yellow-500/40"
            />
            <span
              className="text-2xl tracking-[0.3em]"
              style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A" }}
            >
              BRAF&nbsp;ZAK
            </span>
          </div>
          <p className="text-white/60 max-w-sm">{t.footer_tagline}</p>
          <p className="text-yellow-300/70 text-sm mt-3" data-testid="footer-location">
            📍 Conakry · Guinée
          </p>
        </div>

        <div>
          <h4
            className="text-xs tracking-[0.3em] text-yellow-400 mb-4"
            style={{ fontFamily: '"Oswald", system-ui' }}
          >
            {t.footer_links}
          </h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop" className="text-white/70 hover:text-yellow-300">{t.nav_shop}</Link></li>
            <li><Link to="/lookbook" className="text-white/70 hover:text-yellow-300">{t.nav_lookbook}</Link></li>
            <li><Link to="/about" className="text-white/70 hover:text-yellow-300">{t.nav_about}</Link></li>
            <li><Link to="/contact" className="text-white/70 hover:text-yellow-300">{t.nav_contact}</Link></li>
          </ul>
        </div>

        <div>
          <h4
            className="text-xs tracking-[0.3em] text-yellow-400 mb-4"
            style={{ fontFamily: '"Oswald", system-ui' }}
          >
            {t.footer_social}
          </h4>
          <div className="flex flex-wrap gap-3">
            {socials.map(({ Icon, href, label, id }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                data-testid={`social-${id}`}
                className="w-10 h-10 rounded-full border border-yellow-500/30 flex items-center justify-center text-yellow-300 hover:border-yellow-400 hover:text-yellow-200 hover:scale-105 transition-all"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-yellow-600/15">
        <div className="max-w-7xl mx-auto px-6 py-5 text-center text-xs text-white/40 tracking-[0.2em] uppercase">
          © {new Date().getFullYear()} Braf_Zak · {t.footer_rights}
        </div>
      </div>
    </footer>
  );
}
